import { BenqiSavaxWithdrawalAbi } from '../abis/benqi'
import type { LstWithdrawalRequest } from '../types'
import {
  computeStatus,
  toBigInt,
  toNumber,
  type LstWithdrawalReader,
} from './shared'

/**
 * BENQI sAVAX — paginated unlock-requests. 15-day cooldown + 2-day
 * claim window.
 *
 * Two stages:
 *  1. `getUnlockRequestCount(user) + cooldownPeriod() + redeemPeriod()`
 *  2. `getPaginatedUnlockRequests(user, 0, count)`
 *
 * Surfaces both `readyAt` (cooldown end) and `expiresAt` (claim
 * window close). After `expiresAt` the request becomes `expired` and
 * the unlock is forfeited.
 */
export const readerBenqi: LstWithdrawalReader = {
  fetch: async (user, multicallRetry, chainId, entry) => {
    if (!entry.withdrawalContract) return []

    const stage1 = (await multicallRetry({
      chain: chainId,
      calls: [
        {
          address: entry.withdrawalContract,
          name: 'getUnlockRequestCount',
          params: [user],
        },
        {
          address: entry.withdrawalContract,
          name: 'cooldownPeriod',
          params: [],
        },
        {
          address: entry.withdrawalContract,
          name: 'redeemPeriod',
          params: [],
        },
      ] as unknown as never[],
      abi: [
        BenqiSavaxWithdrawalAbi,
        BenqiSavaxWithdrawalAbi,
        BenqiSavaxWithdrawalAbi,
      ] as unknown as never,
    })) as unknown[]
    const count = toNumber(stage1[0]) ?? 0
    const cooldown = toNumber(stage1[1]) ?? 0
    const claimWindow = toNumber(stage1[2]) ?? 0
    if (count === 0) return []

    const stage2 = (await multicallRetry({
      chain: chainId,
      calls: [
        {
          address: entry.withdrawalContract,
          name: 'getPaginatedUnlockRequests',
          params: [user, 0n, BigInt(count)],
        },
      ] as unknown as never[],
      abi: [BenqiSavaxWithdrawalAbi] as unknown as never,
    })) as unknown[]
    const reqs = stage2[0] as Array<Record<string, unknown>> | undefined
    if (!Array.isArray(reqs)) return []

    const out: LstWithdrawalRequest[] = []
    for (let i = 0; i < reqs.length; i++) {
      const r = reqs[i]
      const startedAt = toNumber(r.startedAt)
      const shareAmount = toBigInt(r.shareAmount)
      if (startedAt === undefined || shareAmount === undefined) continue
      const readyAt = startedAt + cooldown
      const expiresAt = readyAt + claimWindow
      out.push({
        lst: entry.lst,
        brand: entry.brand,
        symbol: entry.symbol,
        requestId: String(i),
        // Note: shareAmount is in sAVAX shares, not AVAX. Convert at
        // claim time using sAVAX.getPooledAvaxByShares() — the
        // amountUnderlying here is the share amount the user
        // committed; UI should label accordingly.
        amountUnderlying: shareAmount.toString(),
        status: computeStatus(readyAt, expiresAt),
        readyAt,
        expiresAt,
      })
    }
    return out
  },
}
